import {
  clampChroma,
  formatHex,
  type Oklch,
  oklch,
  p3,
  parse,
  rgb,
} from "culori";

export interface Color {
  readonly c: number;
  readonly h: number;
  readonly l: number;
}

export type ColorSpace = "srgb" | "p3";

// biome-ignore assist/source/useSortedKeys: Insertion order defines the navigation or dropdown order.
export const colorSpaceDisplay: Record<ColorSpace, string> = {
  srgb: "sRGB",
  p3: "Display P3",
};

const gamutMode = (space: ColorSpace): "rgb" | "p3" =>
  space === "srgb" ? "rgb" : "p3";

const LIGHT_THRESHOLD = 0.6;

const GAMUT_EPSILON = 1e-6;

export function color(l: number, c: number, h: number): Color {
  return {
    c: Math.max(0, c),
    h: ((h % 360) + 360) % 360,
    l: Math.min(1, Math.max(0, l)),
  };
}

export function normalizeHex(input: string): string {
  const trimmed = input.trim();
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

export function parseOklch(input: string): Oklch | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = parse(trimmed);
  if (!parsed) {
    return null;
  }

  const converted = oklch(parsed);
  return converted && typeof converted.l === "number" ? converted : null;
}

export function parseColor(input: string): Color | null {
  const parsed = parseOklch(input);
  if (!parsed) {
    return null;
  }
  const value = color(parsed.l, parsed.c ?? 0, parsed.h ?? 0);
  return [value.l, value.c, value.h].every(Number.isFinite) ? value : null;
}

export function alphaSuffix(alpha: number | undefined): string {
  if (alpha === undefined) {
    return "";
  }
  const a = formatAxis(Math.min(1, Math.max(0, alpha)));
  return a === "1" ? "" : ` / ${a}`;
}

export function toCss(value: Color, alpha?: number): string {
  return `oklch(${formatAxis(value.l)} ${formatAxis(value.c)} ${formatAxis(value.h)}${alphaSuffix(alpha)})`;
}

export function toHex(value: Color): string {
  return formatHex(clampChroma(toCulori(value), "oklch", "rgb"));
}

export function maxChroma(l: number, h: number, space: ColorSpace): number {
  if (l >= 1 || l <= 0) {
    return 0;
  }
  return clampChroma({ c: 1, h, l, mode: "oklch" }, "oklch", gamutMode(space))
    .c;
}

export function clampToGamut(value: Color, space: ColorSpace): Color {
  const clamped = clampChroma(toCulori(value), "oklch", gamutMode(space));
  return color(clamped.l, clamped.c, clamped.h ?? value.h);
}

export function isInGamut(value: Color, space: ColorSpace): boolean {
  const converted =
    space === "srgb" ? rgb(toCulori(value)) : p3(toCulori(value));

  return [converted.r, converted.g, converted.b].every(
    (channel) => channel >= -GAMUT_EPSILON && channel <= 1 + GAMUT_EPSILON
  );
}

export function isLight(value: Color): boolean {
  return value.l > LIGHT_THRESHOLD;
}

export function hueDistance(a: number, b: number): number {
  const difference = (((a - b) % 360) + 360) % 360;
  return Math.min(difference, 360 - difference);
}

export function oklchToRgb255(
  l: number,
  c: number,
  h: number
): { r: number; g: number; b: number } {
  const converted = rgb({ c, h, l, mode: "oklch" });

  return {
    b: channel255(converted.b),
    g: channel255(converted.g),
    r: channel255(converted.r),
  };
}

export const channel255 = (value: number): number =>
  Math.round(Math.min(1, Math.max(0, value)) * 255);

function toCulori(value: Color): Oklch {
  return { c: value.c, h: value.h, l: value.l, mode: "oklch" };
}

export const formatAxis = (value: number, precision = 3): string =>
  String(Number(value.toFixed(precision)));
